import { useMoneyFormatter } from "@/lib/money-format";
/**
 * /companies/:companyId/reportes — Reports overview with summary cards.
 * Links to each detail report.
 */
import { useEffect } from "react";
import { Link, createFileRoute } from "@tanstack/react-router";
import { BarChart3, Percent, Receipt, Scale, TrendingUp } from "lucide-react";
import { useTranslation } from "react-i18next";
import { RequireAuth } from "@/components/RequireAuth";
import { AppShell } from "@/components/layout/AppShell";
import { Skeleton } from "@/components/ui/skeleton";
import { useActiveCompany } from "@/lib/company-context";
import { useReportsSummary } from "@/lib/api/reports.queries";
import { cn } from "@/lib/cn";

export const Route = createFileRoute("/companies/$companyId/reportes/")({
  component: ReportesIndexPage,
});



function ReportesIndexPage() {
  return (
    <RequireAuth>
      <AppShell>
        <ReportesIndexContent />
      </AppShell>
    </RequireAuth>
  );
}

function ReportesIndexContent() {
  const formatCOP = useMoneyFormatter();
  const { t } = useTranslation();
  const { companyId } = Route.useParams();
  const { setActiveCompanyId } = useActiveCompany();

  useEffect(() => {
    if (companyId) setActiveCompanyId(companyId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  // Get current month range
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const lastDay = new Date(y, now.getMonth() + 1, 0).getDate();
  const periodFrom = `${y}-${m}-01`;
  const periodTo = `${y}-${m}-${String(lastDay).padStart(2, "0")}`;

  const { data: summary, isLoading } = useReportsSummary(companyId, { periodFrom, periodTo });

  const reportLinks = [
    {
      to: `/companies/${companyId}/reportes/estado-resultados`,
      icon: TrendingUp,
      title: t("nav.reportes.estado_resultados"),
      description: t("reports.pl.description"),
      color: "text-emerald-600",
      bgColor: "bg-emerald-50 dark:bg-emerald-950",
    },
    {
      to: `/companies/${companyId}/reportes/balance-general`,
      icon: Scale,
      title: t("nav.reportes.balance_general"),
      description: t("reports.bs.description"),
      color: "text-blue-600",
      bgColor: "bg-blue-50 dark:bg-blue-950",
    },
    {
      to: `/companies/${companyId}/reportes/iva`,
      icon: Receipt,
      title: t("nav.reportes.iva"),
      description: t("reports.iva.description"),
      color: "text-amber-600",
      bgColor: "bg-amber-50 dark:bg-amber-950",
    },
    {
      to: `/companies/${companyId}/reportes/retenciones`,
      icon: Percent,
      title: t("nav.reportes.retenciones"),
      description: t("reports.withholdings.description"),
      color: "text-purple-600",
      bgColor: "bg-purple-50 dark:bg-purple-950",
    },
  ];

  return (
    <div className="space-y-6 p-6">
      <div>
        <h1 className="flex items-center gap-2 text-2xl font-bold">
          <BarChart3 className="h-6 w-6" />
          {t("reports.title")}
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">{t("reports.subtitle")}</p>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-5">
        {isLoading ? (
          Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-24 rounded-lg" />
          ))
        ) : summary ? (
          <>
            <SummaryCard
              label={t("reports.summary.revenue")}
              value={formatCOP(summary.revenue)}
              positive={summary.revenue >= 0}
            />
            <SummaryCard
              label={t("reports.summary.expenses")}
              value={formatCOP(summary.expenses)}
              positive={false}
              neutral
            />
            <SummaryCard
              label={t("reports.summary.discounts", {
                count: summary.discountsGranted.invoiceCount,
              })}
              value={formatCOP(summary.discountsGranted.total)}
              positive={false}
              neutral
            />
            <SummaryCard
              label={t("reports.summary.net_profit")}
              value={formatCOP(summary.netProfit)}
              positive={summary.netProfit >= 0}
            />
            <SummaryCard
              label={t("reports.summary.iva_payable")}
              value={formatCOP(summary.ivaPayable)}
              positive={summary.ivaPayable <= 0}
              neutral={summary.ivaPayable === 0}
            />
          </>
        ) : null}
      </div>

      {/* Report links */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {reportLinks.map((link) => {
          const Icon = link.icon;
          return (
            <Link
              key={link.to}
              to={link.to}
              className="group flex items-start gap-4 rounded-xl border bg-card p-5 transition-all hover:border-primary/30 hover:shadow-sm"
            >
              <div className={cn("rounded-lg p-2.5", link.bgColor)}>
                <Icon className={cn("h-5 w-5", link.color)} />
              </div>
              <div>
                <p className="font-semibold group-hover:text-primary">{link.title}</p>
                <p className="mt-0.5 text-xs text-muted-foreground">{link.description}</p>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}

function SummaryCard({
  label,
  value,
  positive,
  neutral = false,
}: {
  label: string;
  value: string;
  positive: boolean;
  neutral?: boolean;
}) {
  return (
    <div className="rounded-xl border bg-card p-4">
      <p className="text-xs font-medium text-muted-foreground">{label}</p>
      <p
        className={cn(
          "mt-2 text-xl font-bold",
          neutral ? "text-foreground" : positive ? "text-emerald-600" : "text-destructive",
        )}
      >
        {value}
      </p>
    </div>
  );
}
